import type { CoreMessage } from "ai";
import { isAgentChatBackend, isClaudeConfigured } from "@/env";
import { useClaudeSettings } from "@/stores/useClaudeSettings";
import { streamAgentReply } from "./agent-api";
import { streamXaesReply } from "./vercel-ai";

/**
 * AI grouping of free-text questionnaire answers into labeled themes —
 * consumed by the Asset Locker's Questionnaire Results modal.
 *
 * Transport mirrors the XAES Analyst chat's backend selector
 * (`use-chat.ts`): the `agent` build routes through the BFF
 * (`streamAgentReply`, ZITADEL token, key stays server-side — the
 * production path); otherwise the browser-direct AI SDK call
 * (`streamXaesReply`) with the facilitator's chosen model. When neither
 * is available (`isClusteringAvailable()` false) the results modal
 * simply shows the raw answers — grouping is an enhancement, never a
 * dependency.
 *
 * The model streams text; we accumulate and parse the trailing JSON
 * object. No JSON-mode helper exists in this codebase — same
 * accumulate-then-parse approach as the insight extraction.
 */

export type AnswerTheme = {
  /** Short human label for the theme, e.g. "Trust & accountability". */
  label: string;
  /** How many answers fall under this theme. */
  count: number;
  /** One representative answer, verbatim. */
  example: string;
};

/** Token guards — clustering input is bounded, not open-ended chat. */
const MAX_ANSWERS = 100;
const MAX_ANSWER_CHARS = 300;

export function isClusteringAvailable(): boolean {
  return isAgentChatBackend() || isClaudeConfigured();
}

function buildPrompt(question: string, answers: string[]): string {
  const numbered = answers
    .map((a, i) => `${i + 1}. ${a.slice(0, MAX_ANSWER_CHARS)}`)
    .join("\n");
  return `Survey question: "${question}"

Responses (${answers.length}):
${numbered}

Group these responses into 2-5 themes of similar meaning. Every response belongs to exactly one theme; counts must sum to ${answers.length}.

Return ONLY a JSON object, no markdown fence, no preamble, exactly this shape:
{"themes":[{"label":"<short theme name>","count":<number of responses>,"example":"<one representative response, verbatim>"}]}`;
}

const SYSTEM =
  "You are a workshop facilitation analyst. You group short survey answers into concise themes and reply with strict JSON only.";

/**
 * Cluster free-text answers into labeled themes. Throws on transport or
 * parse failure (the modal renders the error + keeps the raw list);
 * returns at least one theme for non-empty input.
 */
export async function clusterAnswers({
  question,
  answers,
  signal,
}: {
  question: string;
  answers: string[];
  signal: AbortSignal;
}): Promise<AnswerTheme[]> {
  const bounded = answers
    .map((a) => a.trim())
    .filter((a) => a.length > 0)
    .slice(0, MAX_ANSWERS);
  if (bounded.length === 0) return [];

  const messages: CoreMessage[] = [
    { role: "user", content: buildPrompt(question, bounded) },
  ];

  const stream = isAgentChatBackend()
    ? streamAgentReply({ system: SYSTEM, messages, signal })
    : streamXaesReply({
        system: SYSTEM,
        messages,
        signal,
        model: useClaudeSettings.getState().model,
      });

  let acc = "";
  for await (const delta of stream) {
    acc += delta;
  }
  return parseThemes(acc);
}

/**
 * Extract + validate the themes JSON from the accumulated reply. Tolerates
 * pre/post amble by parsing the outermost `{...}` span. Exported for tests.
 */
export function parseThemes(raw: string): AnswerTheme[] {
  const start = raw.indexOf("{");
  const end = raw.lastIndexOf("}");
  if (start === -1 || end <= start) {
    throw new Error("The model reply contained no JSON object.");
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw.slice(start, end + 1));
  } catch {
    throw new Error("The model reply wasn't valid JSON.");
  }
  const themesRaw =
    typeof parsed === "object" && parsed !== null
      ? (parsed as { themes?: unknown }).themes
      : undefined;
  if (!Array.isArray(themesRaw)) {
    throw new Error('The model reply had no "themes" array.');
  }
  const themes: AnswerTheme[] = [];
  for (const t of themesRaw) {
    if (typeof t !== "object" || t === null) continue;
    const { label, count, example } = t as Record<string, unknown>;
    if (typeof label !== "string" || label.trim().length === 0) continue;
    themes.push({
      label: label.trim(),
      count:
        typeof count === "number" && Number.isFinite(count)
          ? Math.max(0, Math.round(count))
          : 0,
      example: typeof example === "string" ? example : "",
    });
  }
  if (themes.length === 0) {
    throw new Error("The model reply contained no usable themes.");
  }
  return themes;
}
